import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { TabAccounting, TabEmployees, TabFormula, TabSignature } from '../components/PayrollDetailTabs';
import { useAuth } from '../context/AuthContext';
import { payrollsApi, type Payroll, type PayrollStatus } from '../lib/api/payrolls';
import { formatDate, formatVND } from '../lib/format';

const STATUS_LABEL: Record<PayrollStatus, string> = {
  DRAFT: 'Nháp',
  PENDING_KTT: 'Chờ KTT duyệt',
  PENDING_CEO: 'Chờ CEO ký',
  APPROVED: 'Đã duyệt · sẵn sàng chi',
  DISBURSED: 'Đã chi lương',
  REJECTED: 'Bị từ chối',
};

const STATUS_TONE: Record<PayrollStatus, string> = {
  DRAFT: 'gray', PENDING_KTT: 'yellow', PENDING_CEO: 'blue',
  APPROVED: 'green', DISBURSED: 'green', REJECTED: 'red',
};

type TabKey = 'overview' | 'employees' | 'formula' | 'signature' | 'accounting';
type PayrollAction = 'save' | 'export' | 'submit' | 'approve' | 'reject' | 'disburse' | 'send';

interface Step {
  label: string;
  state: 'done' | 'current' | 'upcoming' | 'rejected';
  meta?: string;
  actor?: string;
}

function buildSteps(p: Payroll): Step[] {
  const s = p.status;
  const isDone = (arr: string[]) => arr.includes(s);
  const fmtShort = (d?: string | null) => d ? new Date(d).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  return [
    { label: 'NV lập', state: 'done', meta: fmtShort(p.createdAt), actor: p.createdBy },
    { label: 'Submit', state: s === 'DRAFT' ? 'upcoming' : 'done', meta: p.submittedAt ? fmtShort(p.submittedAt) : (s === 'DRAFT' ? 'chưa gửi' : ''), actor: p.createdBy },
    {
      label: 'KTT duyệt',
      state: s === 'REJECTED' ? 'rejected'
        : isDone(['PENDING_CEO', 'APPROVED', 'DISBURSED']) ? 'done'
        : s === 'PENDING_KTT' ? 'current' : 'upcoming',
      meta: s === 'PENDING_KTT' ? 'Hiện tại' : (isDone(['PENDING_CEO', 'APPROVED', 'DISBURSED']) ? 'Đã duyệt' : 'chưa tới'),
      actor: 'Nguyễn Thị Hà',
    },
    {
      label: 'CEO ký',
      state: isDone(['APPROVED', 'DISBURSED']) ? 'done'
        : s === 'PENDING_CEO' ? 'current' : 'upcoming',
      meta: s === 'PENDING_CEO' ? 'Hiện tại' : (isDone(['APPROVED', 'DISBURSED']) ? fmtShort(p.approvedAt) : 'chưa tới'),
      actor: 'Phạm Minh Đức',
    },
    {
      label: 'Chi lương',
      state: s === 'DISBURSED' ? 'done' : 'upcoming',
      meta: s === 'DISBURSED' ? fmtShort(p.disbursedAt) : 'chưa tới',
      actor: 'Hạch toán',
    },
  ];
}

function pendingHours(from?: string | null): string {
  if (!from) return '';
  const h = Math.max(0, Math.floor((Date.now() - new Date(from).getTime()) / 3600000));
  if (h < 24) return `${h} giờ`;
  return `${Math.floor(h / 24)} ngày`;
}

function initials(name: string): string {
  return (name || '?').split(/\s+/).slice(-2).map((w) => w[0]).join('').toUpperCase();
}

function deptColor(dept: string): string {
  const palette = ['#2563EB', '#7c3aed', '#dc2626', '#16a34a', '#f59e0b', '#0d9488'];
  const h = dept.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return palette[h % palette.length];
}

export function PayrollDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<Payroll | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [sendingPayslips, setSendingPayslips] = useState(false);
  const [confirmAction, setConfirmAction] = useState<PayrollAction | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('overview');

  const load = () => {
    if (!id) return;
    setLoading(true); setErr(null);
    payrollsApi.findOne(id).then(setData)
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const isKtt = user?.role === 'KTT';
  const isCeo = user?.role === 'CEO';
  const isNvKt = user?.role === 'NV_KT';
  const canSubmit = data?.status === 'DRAFT' && (isNvKt || isKtt);
  const canKttApprove = data?.status === 'PENDING_KTT' && isKtt;
  const canCeoApprove = data?.status === 'PENDING_CEO' && isCeo;
  const canReject = ['PENDING_KTT', 'PENDING_CEO'].includes(data?.status ?? '') && (isKtt || isCeo);
  const canDisburse = data?.status === 'APPROVED' && (isNvKt || isKtt);

  const doAction = async (action: 'submit' | 'approve' | 'reject' | 'disburse') => {
    if (!data) return;
    setBusy(action); setErr(null);
    try {
      if (action === 'submit') await payrollsApi.submit(data.id);
      else if (action === 'approve') await payrollsApi.approve(data.id);
      else if (action === 'reject') await payrollsApi.reject(data.id);
      else if (action === 'disburse') await payrollsApi.disburse(data.id);
      load();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const sendPayslips = async () => {
    if (!data) return;
    setSendingPayslips(true); setErr(null);
    try {
      const receipt = await payrollsApi.sendPayslips(data.id);
      const queued = receipt.sent.filter((item) => item.status === 'queued').length;
      const missing = receipt.sent.length - queued;
      setActionMessage(`Đã gửi phiếu lương cho ${queued}/${receipt.sent.length} nhân viên${missing ? ` · ${missing} nhân viên chưa có email` : ''}.`);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setSendingPayslips(false); }
  };

  const executeConfirmedAction = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    if (action === 'save') setActionMessage('Bảng lương hiện đang ở trạng thái Nháp. Bạn có thể tiếp tục chỉnh sửa trước khi gửi duyệt.');
    else if (action === 'export') setActionMessage('Yêu cầu xuất Excel đã được ghi nhận. Tệp xuất sẽ bao gồm các dòng lương theo quyền của người thực hiện.');
    else if (action === 'send') await sendPayslips();
    else await doAction(action);
  };
  const actionContent: Record<PayrollAction, { title: string; description: string; confirm: string; tone?: 'danger' }> = {
    save: { title: 'Lưu bảng lương ở trạng thái nháp?', description: 'Bảng lương vẫn có thể chỉnh sửa trước khi gửi KTT duyệt.', confirm: 'Lưu nháp' },
    export: { title: 'Xuất bảng lương ra Excel?', description: 'Hệ thống sẽ tạo tệp bao gồm chi tiết thu nhập và khấu trừ của nhân viên.', confirm: 'Xuất Excel' },
    submit: { title: 'Gửi bảng lương để KTT duyệt?', description: 'Bảng lương sẽ được khóa chỉnh sửa và chuyển sang trạng thái Chờ KTT duyệt.', confirm: 'Gửi duyệt' },
    approve: { title: canCeoApprove ? 'CEO ký duyệt bảng lương?' : 'Duyệt và chuyển CEO?', description: canCeoApprove ? 'Bảng lương được phê duyệt để nhân viên kế toán thực hiện chi lương.' : 'Bảng lương sẽ chuyển đến CEO để ký duyệt cuối.', confirm: canCeoApprove ? 'Ký duyệt' : 'Duyệt & chuyển CEO' },
    reject: { title: 'Trả lại bảng lương?', description: 'Bảng lương sẽ quay về trạng thái cần điều chỉnh trước khi gửi duyệt lại.', confirm: 'Xác nhận trả lại', tone: 'danger' },
    disburse: { title: `Xác nhận chi lương ${data ? formatVND(data.net) : ''}?`, description: 'Hệ thống sẽ tạo phiếu chi trong Sổ quỹ và bút toán trong Sổ cái. Thao tác này không thể hoàn tác từ bảng lương.', confirm: 'Xác nhận chi lương' },
    send: { title: 'Gửi phiếu lương qua email?', description: 'Chỉ các nhân viên đã nhận email mới xem được phiếu lương của chính mình.', confirm: 'Gửi phiếu lương' },
  };

  const netDeltaPct = data && data.prevPeriodNet > 0
    ? Math.round(((data.net - data.prevPeriodNet) / data.prevPeriodNet) * 1000) / 10
    : null;

  const bhBreakdown = data ? [
    { code: 'BHXH', pct: '17%', amount: Math.round(data.basicSalary * 0.17) },
    { code: 'BHYT', pct: '3%', amount: Math.round(data.basicSalary * 0.03) },
    { code: 'BHTN', pct: '1%', amount: Math.round(data.basicSalary * 0.01) },
    { code: 'KPCĐ', pct: '2%', amount: Math.round(data.basicSalary * 0.02) },
  ] : [];

  const totalCompanyCost = data ? data.gross + data.bhCompany : 0;
  const timeSince = pendingHours(data?.submittedAt || data?.createdAt);

  return (
    <AppShell hideTitle>
      <div className="pdp2">
        {/* Sticky top bar: breadcrumb + title + status + actions */}
        <div className="pdp2-top">
          <div className="pdp2-top-left">
            <button type="button" className="pdp2-back" onClick={() => navigate('/payroll')} title="Quay lại">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></svg>
            </button>
            <div className="pdp2-crumb-wrap">
              <div className="pdp2-crumb">KẾ TOÁN › BẢNG LƯƠNG</div>
              <div className="pdp2-title-row">
                <h1>{data?.code ?? 'Đang tải…'}</h1>
                {data && <span className={`pyr-status pyr-status-${STATUS_TONE[data.status]}`}>{STATUS_LABEL[data.status]}</span>}
              </div>
            </div>
          </div>
          <div className="pdp2-top-actions">
            <button type="button" className="pay-btn-outline" onClick={() => window.print()}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect width="12" height="8" x="6" y="14" /><path d="M6 9V2h12v7" /></svg>
              In
            </button>
            <button type="button" className="pay-btn-outline" onClick={() => setConfirmAction('export')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
              Xuất Excel
            </button>
            <button type="button" className="pay-btn-outline" onClick={() => setTab('signature')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 8v6M23 11h-6" /><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
              Xem trình ký
            </button>
            <button type="button" className="pay-btn-outline" onClick={() => setConfirmAction('send')} disabled={sendingPayslips || data?.status !== 'DISBURSED'} title={data?.status !== 'DISBURSED' ? 'Chỉ gửi sau khi đã chi lương' : undefined}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
              {sendingPayslips ? 'Đang gửi…' : 'Gửi phiếu lương'}
            </button>
          </div>
        </div>

        {err && <div className="pay-table-error" style={{ margin: '16px 0' }}>{err}</div>}

        {loading && !data && <div className="pay-empty">Đang tải…</div>}

        {data && (
          <>
            <section className={`pdp2-detail-section ${tab === 'overview' ? 'is-overview' : ''}`}>
            {/* Thông tin bảng lương + tabs */}
            <div className="pdp2-detail-header">
              <div className="pdp2-meta">
                <div><span>Kỳ lương</span><b>{data.period}</b></div>
                <div>
                  <span>Bộ phận</span>
                  <div className="pdp2-meta-with-icon">
                    <span className="pdp2-dept-dot" style={{ background: deptColor(data.department) }} />
                    <b>{data.department}</b>
                  </div>
                </div>
                <div>
                  <span>Loại</span>
                  <div className="pdp2-meta-with-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><rect width="16" height="20" x="4" y="2" rx="2" /><line x1="8" x2="16" y1="6" y2="6" /><line x1="8" x2="16" y1="10" y2="10" /></svg>
                    <b>{data.type}</b>
                  </div>
                </div>
                <div>
                  <span>Số nhân viên</span>
                  <div className="pdp2-meta-with-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
                    <b>{data.employeeCount}</b>
                  </div>
                </div>
                <div>
                  <span>Người lập</span>
                  <div className="pdp2-meta-with-icon">
                    <div className="pdp2-avatar-mini" style={{ background: deptColor(data.createdBy) }}>{initials(data.createdBy)}</div>
                    <b>{data.createdBy}</b>
                  </div>
                </div>
                <div><span>Ngày lập</span><b>{formatDate(data.createdAt)}</b></div>
              </div>

              <div className="pdp2-tabs" role="tablist" aria-label="Nội dung bảng lương">
                <button className={`pdp2-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>Tổng quan</button>
                <button className={`pdp2-tab ${tab === 'employees' ? 'active' : ''}`} onClick={() => setTab('employees')}>
                  Chi tiết nhân viên
                  <span className="pdp2-tab-badge">{data.employeeCount}</span>
                </button>
                <button className={`pdp2-tab ${tab === 'formula' ? 'active' : ''}`} onClick={() => setTab('formula')}>Công thức</button>
                <button className={`pdp2-tab ${tab === 'signature' ? 'active' : ''}`} onClick={() => setTab('signature')}>Trình ký</button>
                <button className={`pdp2-tab ${tab === 'accounting' ? 'active' : ''}`} onClick={() => setTab('accounting')}>Hạch toán</button>
              </div>
            </div>

            {tab === 'overview' && (
              <>
                {/* Tiến trình duyệt */}
                <section className="pdp2-overview-block pdp2-approval-block">
                  <div className="pdp2-approval-head">
                    <div className="pdp2-approval-title">
                      <div className="pdp2-approval-icon">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                      </div>
                      <h3>Tiến trình duyệt</h3>
                      {(canKttApprove || canCeoApprove) && (
                        <span className="pdp2-pending-pill">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                          Đang chờ {canKttApprove ? 'KTT duyệt' : 'CEO ký'} · {timeSince}
                        </span>
                      )}
                    </div>
                    <div className="pdp2-approval-actions">
                      {canReject && (
                        <button type="button" className="pdp2-btn-return" onClick={() => setConfirmAction('reject')} disabled={busy !== null}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 14 6-6M15 14l-6-6" /><circle cx="12" cy="12" r="10" /></svg>
                          {busy === 'reject' ? 'Đang trả…' : 'Trả lại'}
                        </button>
                      )}
                      {canSubmit && (
                        <button type="button" className="pdp2-btn-approve" onClick={() => setConfirmAction('submit')} disabled={busy !== null}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                          {busy === 'submit' ? 'Đang gửi…' : 'Gửi duyệt → KTT'}
                        </button>
                      )}
                      {canKttApprove && (
                        <button type="button" className="pdp2-btn-approve" onClick={() => setConfirmAction('approve')} disabled={busy !== null}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                          {busy === 'approve' ? 'Đang duyệt…' : 'Duyệt & chuyển CEO'}
                        </button>
                      )}
                      {canCeoApprove && (
                        <button type="button" className="pdp2-btn-approve" onClick={() => setConfirmAction('approve')} disabled={busy !== null}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                          {busy === 'approve' ? 'Đang ký…' : 'CEO ký duyệt'}
                        </button>
                      )}
                      {canDisburse && (
                        <button type="button" className="pay-btn-dark" onClick={() => setConfirmAction('disburse')} disabled={busy !== null}>
                          {busy === 'disburse' ? 'Đang chi…' : `Chi lương ${formatVND(data.net)}`}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="pdp2-stepper">
                    {buildSteps(data).map((s, i, arr) => (
                      <div key={i} className={`pdp2-step pdp2-step-${s.state}`}>
                        <div className="pdp2-step-line-wrap">
                          <div className="pdp2-step-circle">
                            {s.state === 'done' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                            ) : s.state === 'rejected' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                            ) : s.state === 'current' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                            ) : (
                              i + 1
                            )}
                          </div>
                          {i < arr.length - 1 && <div className="pdp2-step-connector" />}
                        </div>
                        <div className="pdp2-step-content">
                          <div className="pdp2-step-label">{s.label}</div>
                          {s.meta && <div className="pdp2-step-meta">{s.meta}</div>}
                          {s.actor && <div className="pdp2-step-actor">{s.actor}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* 3 stat cards match prototype */}
                <div className="pdp2-overview-block pdp2-overview-stat-grid">
                <div className="pdp2-stat-grid">
                  <div className="pdp2-stat blue">
                    <div className="pdp2-stat-stripe" />
                    <div className="pdp2-stat-head">
                      <div className="pdp2-stat-icon">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                      </div>
                      <div className="pdp2-stat-label">Tổng thu nhập</div>
                    </div>
                    <div className="pdp2-stat-value">{formatVND(data.gross)}</div>
                    <ul className="pdp2-stat-lines">
                      <li><span>Lương thoả thuận</span><span>{formatVND(data.basicSalary)}</span></li>
                      <li><span>Phụ cấp miễn thuế</span><span>{formatVND(data.allowanceExempt)}</span></li>
                      <li><span>Phụ cấp không miễn thuế</span><span>{formatVND(data.allowanceTaxable)}</span></li>
                      <li><span>Thưởng / Bonus</span><span>{formatVND(data.bonus)}</span></li>
                    </ul>
                  </div>

                  <div className="pdp2-stat red">
                    <div className="pdp2-stat-stripe" />
                    <div className="pdp2-stat-head">
                      <div className="pdp2-stat-icon">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5-7 7 7 7" /></svg>
                      </div>
                      <div className="pdp2-stat-label">Tổng khấu trừ</div>
                    </div>
                    <div className="pdp2-stat-value">{formatVND(data.gross - data.net)}</div>
                    <ul className="pdp2-stat-lines">
                      <li><span>BH nhân viên đóng (10.5%)</span><span>{formatVND(data.bhEmployee)}</span></li>
                      <li><span>Thuế TNCN</span><span>{formatVND(data.tncn)}</span></li>
                      <li><span>Tạm ứng</span><span>{formatVND(data.advance)}</span></li>
                    </ul>
                  </div>

                  <div className="pdp2-stat green">
                    <div className="pdp2-stat-stripe" />
                    <div className="pdp2-stat-head">
                      <div className="pdp2-stat-icon">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                      </div>
                      <div className="pdp2-stat-label">Thực nhận</div>
                      {netDeltaPct !== null && (
                        <span className="pdp2-stat-delta">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d={netDeltaPct >= 0 ? 'm5 12 7-7 7 7M12 19V5' : 'm5 12 7 7 7-7M12 5v14'} /></svg>
                          {Math.abs(netDeltaPct)}%
                        </span>
                      )}
                    </div>
                    <div className="pdp2-stat-value">{formatVND(data.net)}</div>
                    <ul className="pdp2-stat-lines">
                      <li><span>Số nhân viên</span><span>{data.employeeCount}</span></li>
                      <li><span>Trung bình / NV</span><span>{formatVND(data.employeeCount > 0 ? Math.round(data.net / data.employeeCount) : 0)}</span></li>
                      {netDeltaPct !== null && (
                        <li><span>So kỳ trước</span><span style={{ color: netDeltaPct >= 0 ? '#15803d' : '#b91c1c', fontWeight: 600 }}>{netDeltaPct >= 0 ? '+' : ''}{netDeltaPct}%</span></li>
                      )}
                    </ul>
                  </div>
                </div>
                </div>

                {/* BH company + Allocation — 2col grid */}
                <div className="pdp2-overview-block pdp2-2col">
                  <div className="pdp2-card">
                    <div className="pdp2-sub-card-head">
                      <div className="pdp2-sub-card-icon">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                      </div>
                      <h3>Chi phí công ty đóng (BH bắt buộc)</h3>
                    </div>
                    <table className="pdp2-bh-tbl">
                      <tbody>
                        {bhBreakdown.map((b) => (
                          <tr key={b.code}>
                            <td>{b.code} ({b.pct})</td>
                            <td>{formatVND(b.amount)}</td>
                          </tr>
                        ))}
                        <tr className="total">
                          <td>Tổng</td>
                          <td>{formatVND(data.bhCompany)}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="pdp2-bh-callout">
                      Tổng chi phí cho DN (lương + BH công ty) = <b>{formatVND(totalCompanyCost)}</b>
                    </div>
                  </div>

                  <div className="pdp2-card">
                    <div className="pdp2-sub-card-head">
                      <div className="pdp2-sub-card-icon">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 16V8" /><path d="M11 16v-5" /><path d="M15 16V6" /><path d="M19 16v-3" /></svg>
                      </div>
                      <h3>Phân bổ theo tài khoản</h3>
                    </div>
                    {data.allocationJson && data.allocationJson.length > 0 ? (
                      <>
                        <table className="pdp2-alloc-tbl">
                          <thead>
                            <tr>
                              <th style={{ width: 72 }}>TK</th>
                              <th>Diễn giải</th>
                              <th>Số tiền</th>
                              <th style={{ width: 56 }}>%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.allocationJson.map((a) => {
                              const tone = a.account.startsWith('641') ? 'yellow' : a.account.startsWith('622') ? 'green' : a.account.startsWith('154') ? 'red' : 'blue';
                              return (
                                <tr key={a.account}>
                                  <td><span className={`pdp2-alloc-code pdp2-alloc-code-${tone}`}>{a.account}</span></td>
                                  <td>{a.name}</td>
                                  <td>{formatVND(a.amount)}</td>
                                  <td>{a.pct}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div className="pdp2-alloc-bar">
                          {data.allocationJson.map((a) => {
                            const tone = a.account.startsWith('641') ? 'yellow' : a.account.startsWith('622') ? 'green' : 'blue';
                            return (
                              <div key={a.account} className={`pdp2-alloc-bar-seg ${tone}`} style={{ width: `${a.pct}%` }} title={`${a.account}: ${a.pct}%`} />
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="pay-empty" style={{ padding: 20 }}>Chưa có phân bổ TK</div>
                    )}
                  </div>
                </div>
              </>
            )}

            {tab === 'employees' && <TabEmployees payrollId={data.id} payroll={data} />}
            {tab === 'formula' && <TabFormula payroll={data} />}
            {tab === 'signature' && <TabSignature payroll={data} />}
            {tab === 'accounting' && <TabAccounting payroll={data} />}
            </section>

            {/* Sticky footer bar — recap + primary actions */}
            <div className="pdp2-footer">
              <div className="pdp2-footer-recap">
                <span>Thực nhận: <b>{formatVND(data.net)}</b></span>
                <span>·</span>
                <span>{data.employeeCount} nhân viên</span>
                <span>·</span>
                <span>Trạng thái: <b>{STATUS_LABEL[data.status]}</b></span>
              </div>
              <div className="pdp2-footer-actions">
                {canSubmit && (
                  <button type="button" className="pay-btn-outline" onClick={() => setConfirmAction('save')}>Lưu nháp</button>
                )}
                {canReject && (
                  <button type="button" className="pdp2-btn-return" onClick={() => setConfirmAction('reject')} disabled={busy !== null}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></svg>
                    {busy === 'reject' ? 'Đang trả…' : 'Từ chối'}
                  </button>
                )}
                {(canKttApprove || canCeoApprove) && (
                  <button type="button" className="pdp2-btn-approve" onClick={() => setConfirmAction('approve')} disabled={busy !== null}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                    {busy === 'approve' ? 'Đang duyệt…' : (canCeoApprove ? 'CEO ký duyệt' : 'Duyệt hàng loạt')}
                  </button>
                )}
                {canSubmit && (
                  <button type="button" className="pay-btn-primary" onClick={() => setConfirmAction('submit')} disabled={busy !== null}>
                    {busy === 'submit' ? 'Đang gửi…' : 'Gửi duyệt → KTT'}
                  </button>
                )}
                {canDisburse && (
                  <button type="button" className="pay-btn-dark" onClick={() => setConfirmAction('disburse')} disabled={busy !== null}>
                    {busy === 'disburse' ? 'Đang chi…' : `Chi lương ${formatVND(data.net)}`}
                  </button>
                )}
              </div>
            </div>

            {confirmAction && (
              <div className="pdp2-action-overlay" role="presentation">
                <section className="pdp2-action-modal" role="dialog" aria-modal="true" aria-labelledby="payroll-action-title">
                  <div className={`pdp2-action-icon${actionContent[confirmAction].tone === 'danger' ? ' is-danger' : ''}`}>
                    {confirmAction === 'send' ? '✉' : confirmAction === 'disburse' ? '₫' : confirmAction === 'reject' ? '!' : '✓'}
                  </div>
                  <button type="button" className="pdp2-action-close" onClick={() => setConfirmAction(null)} aria-label="Đóng">×</button>
                  <div className="pdp2-action-copy">
                    <h2 id="payroll-action-title">{actionContent[confirmAction].title}</h2>
                    <p>{actionContent[confirmAction].description}</p>
                    {confirmAction === 'send' && <div className="pdp2-action-note">Phiếu lương chỉ hiển thị cho đúng nhân viên sau khi email được ghi nhận gửi thành công.</div>}
                  </div>
                  <div className="pdp2-action-buttons">
                    <button type="button" className="pay-btn-outline" onClick={() => setConfirmAction(null)}>Hủy</button>
                    <button type="button" className={actionContent[confirmAction].tone === 'danger' ? 'pdp2-action-danger' : 'pay-btn-primary'} onClick={executeConfirmedAction} disabled={busy !== null || sendingPayslips}>
                      {sendingPayslips && confirmAction === 'send' ? 'Đang gửi…' : actionContent[confirmAction].confirm}
                    </button>
                  </div>
                </section>
              </div>
            )}

            {actionMessage && (
              <div className="pdp2-action-overlay" role="presentation">
                <section className="pdp2-action-modal pdp2-action-modal-result" role="dialog" aria-modal="true" aria-labelledby="payroll-action-result-title">
                  <div className="pdp2-action-icon">✓</div>
                  <div className="pdp2-action-copy">
                    <h2 id="payroll-action-result-title">Hoàn tất</h2>
                    <p>{actionMessage}</p>
                  </div>
                  <div className="pdp2-action-buttons">
                    <button type="button" className="pay-btn-primary" onClick={() => setActionMessage(null)}>Đóng</button>
                  </div>
                </section>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
